import { RankedPromoOfferDiscount } from 'src/apollo/onlineOrdering'

// Transitional: backfill V2 from deprecated targetSegment until V2 ships to all rings; remove in OE-254 after.
export const backfillTargetSegmentV2 = (
  offer: RankedPromoOfferDiscount
): RankedPromoOfferDiscount => {
  if (offer.targetSegmentV2 || !offer.targetSegment) return offer
  return {
    ...offer,
    targetSegmentV2: {
      name: offer.targetSegment.name,
      includesGuest: offer.targetSegment.includesGuest
    }
  }
}

/**
 * The guest segment names the UI recognizes, matching the `targetSegmentV2.name`
 * values returned in the ranked promo offer banners. Add a value when the UI
 * needs to treat a new segment specially.
 */
export const GUESTBOOK_QUERY_SEGMENT_NAMES = {
  ALL_GUESTS: 'ALL_GUESTS',
  NEW_GUESTS: 'NEW_GUESTS'
} as const

export type SegmentRestrictionI18nKeys = {
  /** i18n key for the restriction label shown on the card and in the modal */
  restrictions: string
  /** i18n key for the full description shown when the user is ineligible (restrictions + reason combined) */
  restrictionsWhenIneligible: string
}

/**
 * Maps a target segment to the i18n keys for its guest-facing restriction copy
 * (the label shown on the offer card and the fuller text shown when the guest
 * is ineligible). Segments without an entry show no restriction text.
 */
const RESTRICTION_I18N_KEYS_BY_SEGMENT: Partial<
  Record<string, SegmentRestrictionI18nKeys>
> = {
  [GUESTBOOK_QUERY_SEGMENT_NAMES.NEW_GUESTS]: {
    restrictions: 'offers.segments.new-guests-restrictions',
    restrictionsWhenIneligible:
      'offers.segments.new-guests-restrictions-when-ineligible'
  }
}

/**
 * Returns the segment-specific I18n keys for an offer.
 * Returns null if the offer has no meaningful target
 * segment or if the target segment doesn't have I18n keys defined.
 */
export const getSegmentRestrictionI18nKeys = (
  offer: RankedPromoOfferDiscount
): SegmentRestrictionI18nKeys | null => {
  const name = offer.targetSegmentV2?.name
  if (!name) return null
  return RESTRICTION_I18N_KEYS_BY_SEGMENT[name] ?? null
}

export const hasTargetSegment = (offer: RankedPromoOfferDiscount): boolean =>
  !!(
    offer.targetSegmentV2?.name &&
    offer.targetSegmentV2?.name !== GUESTBOOK_QUERY_SEGMENT_NAMES.ALL_GUESTS
  )

export type SegmentedOfferState =
  | 'not-segmented'
  | 'logged-out'
  | 'ineligible'
  | 'eligible'

export const getSegmentedOfferState = (
  offer: RankedPromoOfferDiscount,
  isAuthenticated: boolean
): SegmentedOfferState => {
  if (!hasTargetSegment(offer)) {
    return 'not-segmented'
  }
  if (!isAuthenticated) {
    return 'logged-out'
  }
  if (offer.targetSegmentV2?.includesGuest === true) {
    return 'eligible'
  }
  return 'ineligible'
}
